/**
 * Delete Business Dialog
 * Confirmation dialog with type-to-confirm pattern
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';

interface DeleteBusinessDialogProps {
  open: boolean;
  businessName: string;
  businessSlug: string;
  onClose: () => void;
  onDelete: () => Promise<void>;
}

export function DeleteBusinessDialog({
  open,
  businessName,
  businessSlug,
  onClose,
  onDelete,
}: DeleteBusinessDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmPattern = `${businessName}/${businessSlug}`;
  const isConfirmed = confirmText === confirmPattern;

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await onDelete();
      setConfirmText('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Business</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          <Alert severity="warning">
            This action is permanent and will delete the business, all services, and related data.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2">
            To confirm, type: <strong>{confirmPattern}</strong>
          </Typography>
          <TextField
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            fullWidth
            disabled={loading}
            placeholder={confirmPattern}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading || !isConfirmed}
        >
          {loading ? <CircularProgress size={24} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
